import React, { Component } from 'react';
import { toast } from 'react-toastify';

import * as api from '../api';
import { APPS, DEFAULT_APP } from '../constants';
import FaqImageField from './FaqImageField';

const emptyFaq = (language) => ({
    id: '',
    question: '',
    answer: '',
    keywords: '',
    category: '',
    imageUrls: '',
    language,
    isActive: true,
});

/**
 * Kho kiến thức của chatbot. Chatbot chỉ được trả lời dựa trên những mục ở đây, nên sửa ở đây là
 * cách duy nhất để đổi nội dung nó nói.
 */
export default class FaqTab extends Component {
    constructor(props) {
        super(props);
        this.state = {
            language: DEFAULT_APP,
            faqs: [],
            keyword: '',
            category: '',
            loading: true,
            editing: null,
            isCreate: false,
            saving: false,
            bulkText: '',
            bulkRunning: false,
            showBulk: false,
        };
    }

    componentDidMount() {
        this.load();
    }

    load = () => {
        this.setState({ loading: true });
        api.listFaq(this.state.language)
            .then((faqs) => this.setState({ faqs: Array.isArray(faqs) ? faqs : [], loading: false }))
            .catch(() => {
                this.setState({ faqs: [], loading: false });
                toast.error('Không tải được danh sách FAQ.');
            });
    };

    handleChangeLanguage = (language) => {
        this.setState({ language, category: '' }, this.load);
    };

    openCreate = () => {
        this.setState({ editing: emptyFaq(this.state.language), isCreate: true });
    };

    /** Bấm lại vào đúng mục đang sửa thì đóng ô, khỏi phải với tới nút Đóng. */
    openEdit = (faq) => {
        this.setState((prev) => (
            prev.editing && !prev.isCreate && prev.editing.id === faq.id
                ? { editing: null, isCreate: false }
                : { editing: { ...faq }, isCreate: false }
        ));
    };

    closeEditor = () => {
        this.setState({ editing: null, isCreate: false });
    };

    handleEditChange = (key, value) => {
        this.setState((prev) => ({ editing: { ...prev.editing, [key]: value } }));
    };

    handleSave = () => {
        const { editing, isCreate } = this.state;

        if (!editing.id || !editing.id.trim()) {
            toast.warning('Phải đặt mã FAQ, ví dụ: nap-icoin-momo');
            return;
        }
        if (!editing.question || !editing.question.trim()) {
            toast.warning('Phải nhập câu hỏi mẫu.');
            return;
        }
        if (!editing.answer || !editing.answer.trim()) {
            toast.warning('Phải nhập câu trả lời.');
            return;
        }

        const payload = {
            ...editing,
            id: editing.id.trim(),
        };

        this.setState({ saving: true });
        const request = isCreate ? api.createFaq(payload) : api.updateFaq(payload);

        request
            .then(() => {
                this.setState({ saving: false, editing: null, isCreate: false });
                toast.success(isCreate ? 'Đã thêm FAQ.' : 'Đã cập nhật FAQ.');
                this.load();
            })
            .catch((error) => {
                this.setState({ saving: false });
                toast.error(error.message || 'Không lưu được FAQ.');
            });
    };

    handleDelete = (faq) => {
        if (!window.confirm(`Xoá FAQ "${faq.id}"? Chatbot sẽ không còn dùng nội dung này để trả lời.`)) {
            return;
        }
        api.deleteFaq(faq.id)
            .then(() => {
                toast.success('Đã xoá FAQ.');
                this.load();
            })
            .catch(() => toast.error('Không xoá được FAQ.'));
    };

    /**
     * Nạp hàng loạt bằng JSON. Chạy thử trước để xem sẽ tạo mới và ghi đè những mã nào.
     */
    handleBulk = (dryRun) => {
        let faqs;
        try {
            faqs = JSON.parse(this.state.bulkText);
        } catch (e) {
            toast.error('JSON không hợp lệ.');
            return;
        }
        if (!Array.isArray(faqs) || faqs.length === 0) {
            toast.warning('Cần một mảng JSON có ít nhất một FAQ.');
            return;
        }

        this.setState({ bulkRunning: true });
        api.bulkCreateFaq(faqs, dryRun)
            .then((result) => {
                this.setState({ bulkRunning: false });
                const created = (result && result.createdFaqIds) || [];
                const updated = (result && result.updatedFaqIds) || [];
                toast.success(
                    `${dryRun ? 'Chạy thử' : 'Đã nạp'}: tạo mới ${created.length}, ghi đè ${updated.length}.`
                );
                if (!dryRun) {
                    this.setState({ bulkText: '' });
                    this.load();
                }
            })
            .catch((error) => {
                this.setState({ bulkRunning: false });
                toast.error(error.message || 'Nạp hàng loạt thất bại.');
            });
    };

    categories = () => {
        const set = new Set(this.state.faqs.map((faq) => faq.category).filter(Boolean));
        return Array.from(set).sort();
    };

    filteredFaqs = () => {
        const keyword = this.state.keyword.trim().toLowerCase();
        const { category } = this.state;

        return this.state.faqs.filter((faq) => {
            if (category && faq.category !== category) return false;
            if (!keyword) return true;
            return [faq.id, faq.question, faq.answer, faq.keywords, faq.category]
                .filter(Boolean)
                .some((field) => field.toLowerCase().includes(keyword));
        });
    };

    /**
     * Ô soạn thảo dùng chung cho hai chỗ đặt: thêm mới thì nằm trên đầu danh sách, còn sửa thì mở
     * ngay dưới đúng mục đó để khỏi phải cuộn lên.
     */
    renderEditorFields() {
        const { editing, isCreate } = this.state;

        return (
            <React.Fragment>
                <div className="cs-grid cs-grid--3">
                    <div className="cs-field">
                        <label>Mã FAQ</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="nap-icoin-momo"
                            value={editing.id}
                            disabled={!isCreate}
                            onChange={(e) => this.handleEditChange('id', e.target.value)}
                        />
                        <small className="cs-hint">
                            Không đổi được sau khi tạo. Đây cũng là mã chatbot trích dẫn khi trả lời.
                        </small>
                    </div>

                    <div className="cs-field">
                        <label>Nhóm chủ đề</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="nạp tiền"
                            value={editing.category || ''}
                            onChange={(e) => this.handleEditChange('category', e.target.value)}
                        />
                        <small className="cs-hint">Chỉ để lọc trên trang này</small>
                    </div>


                    <div className="cs-field">
                        <label>Ứng dụng</label>
                        <select
                            className="form-control"
                            value={editing.language}
                            onChange={(e) => this.handleEditChange('language', e.target.value)}
                        >
                            {APPS.map((app) => (
                                <option key={app.value} value={app.value}>{app.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="cs-field">
                    <label>Câu hỏi mẫu</label>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Làm sao nạp icoin bằng Momo?"
                        value={editing.question}
                        onChange={(e) => this.handleEditChange('question', e.target.value)}
                    />
                </div>

                <div className="cs-field">
                    <label>Câu trả lời</label>
                    <textarea
                        className="form-control"
                        rows="6"
                        value={editing.answer}
                        onChange={(e) => this.handleEditChange('answer', e.target.value)}
                    />
                    <small className="cs-hint">
                        Cách nhau một dòng trống thì chatbot gửi thành nhiều tin; xuống dòng thường thì
                        giữ nguyên trong một tin.
                    </small>
                </div>

                <div className="cs-field">
                    <label>Từ khoá phụ</label>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="momo,nạp tiền,nap tien"
                        value={editing.keywords || ''}
                        onChange={(e) => this.handleEditChange('keywords', e.target.value)}
                    />
                    <small className="cs-hint">
                        Ngăn cách bằng dấu phẩy. Giúp chatbot khớp đúng mục khi người dùng hỏi kiểu khác.
                    </small>
                </div>

                <FaqImageField
                    value={editing.imageUrls || ''}
                    onChange={(value) => this.handleEditChange('imageUrls', value)}
                />

                <label className="cs-check" style={{ marginBottom: 0 }}>
                    <input
                        type="checkbox"
                        checked={editing.isActive !== false}
                        onChange={(e) => this.handleEditChange('isActive', e.target.checked)}
                    />
                    <span>
                        <span className="cs-check__text">Đang dùng</span>
                        <small className="cs-hint">
                            Tắt thì chatbot không còn thấy mục này, nhưng dữ liệu vẫn giữ lại.
                        </small>
                    </span>
                </label>
            </React.Fragment>
        );
    }

    renderEditorActions() {
        const { saving } = this.state;

        return (
            <React.Fragment>
                <button
                    type="button"
                    className="btn btn-primary mr-2"
                    onClick={this.handleSave}
                    disabled={saving}
                >
                    <i className="fa-solid fa-floppy-disk mr-1" />
                    {saving ? 'Đang lưu…' : 'Lưu'}
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={this.closeEditor}>
                    Huỷ
                </button>
            </React.Fragment>
        );
    }

    /** Chỉ dùng cho mục thêm mới — mục đang sửa có ô riêng lồng trong bảng. */
    renderCreateEditor() {
        const { editing, isCreate } = this.state;
        if (!editing || !isCreate) return null;

        return (
            <div className="cs-card cs-card--accent">
                <div className="cs-card__head">
                    <div>
                        <h2>
                            <i className="fa-solid fa-plus mr-2" />
                            Thêm câu trả lời mới
                        </h2>
                        <div className="cs-card__desc">
                            Chatbot chỉ nói những gì có ở đây. Viết đúng giọng CSKH, đủ ý, không hứa hẹn.
                        </div>
                    </div>
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={this.closeEditor}
                    >
                        Đóng
                    </button>
                </div>

                <div className="cs-card__body">{this.renderEditorFields()}</div>

                <div className="cs-card__foot">{this.renderEditorActions()}</div>
            </div>
        );
    }

    renderToolbar() {
        const { language, keyword, category } = this.state;

        return (
            <div className="cs-card">
                <div className="cs-card__body">
                    <div className="cs-filters">
                        <div className="cs-field">
                            <label>Ứng dụng</label>
                            <select
                                className="form-control"
                                value={language}
                                onChange={(e) => this.handleChangeLanguage(e.target.value)}
                            >
                                {APPS.map((app) => (
                                    <option key={app.value} value={app.value}>{app.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="cs-field">
                            <label>Nhóm chủ đề</label>
                            <select
                                className="form-control"
                                value={category}
                                onChange={(e) => this.setState({ category: e.target.value })}
                            >
                                <option value="">Tất cả</option>
                                {this.categories().map((item) => (
                                    <option key={item} value={item}>{item}</option>
                                ))}
                            </select>
                        </div>

                        <div className="cs-field" style={{ minWidth: '260px', flex: '1 1 260px' }}>
                            <label>Tìm trong danh sách</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Mã, câu hỏi, câu trả lời, từ khoá…"
                                value={keyword}
                                onChange={(e) => this.setState({ keyword: e.target.value })}
                            />
                        </div>

                        <button type="button" className="btn btn-primary" onClick={this.openCreate}>
                            <i className="fa-solid fa-plus mr-1" />
                            Thêm câu trả lời
                        </button>
                        <button type="button" className="btn btn-outline-secondary" onClick={this.load}>
                            <i className="fa-solid fa-rotate mr-1" />
                            Tải lại
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    renderTable(faqs) {
        const { loading, editing, isCreate } = this.state;
        const editingId = editing && !isCreate ? editing.id : null;

        if (loading) {
            return <div className="cs-loading">Đang tải…</div>;
        }

        if (faqs.length === 0) {
            return (
                <div className="cs-empty">
                    <div className="cs-empty__icon"><i className="fa-solid fa-book" /></div>
                    <div className="cs-empty__title">Chưa có câu trả lời nào</div>
                    <div className="cs-empty__desc">
                        Kho rỗng thì chatbot không có gì để dựa vào, nên sẽ chuyển hết sang nhân viên.
                    </div>
                </div>
            );
        }

        return (
            <div className="cs-table-wrap">
                <table className="cs-table">
                    <thead>
                        <tr>
                            <th style={{ width: '20%' }}>Mã</th>
                            <th style={{ width: '25%' }}>Câu hỏi</th>
                            <th style={{ width: '32%' }}>Câu trả lời</th>
                            <th style={{ width: '11%' }}>Nhóm</th>
                            <th style={{ width: '12%' }}>Thao tác</th>
                        </tr>
                    </thead>
                    <tbody>
                        {faqs.map((faq) => {
                            const isEditing = editingId === faq.id;
                            const rowClass = [
                                faq.isActive === false ? 'is-dim' : '',
                                isEditing ? 'is-editing' : '',
                            ].filter(Boolean).join(' ');

                            return (
                            <React.Fragment key={faq.id}>
                            <tr className={rowClass}>
                                <td>
                                    <div><span className="cs-code">{faq.id}</span></div>
                                    <div style={{ marginTop: '6px' }}>
                                        {faq.isActive === false && (
                                            <span className="cs-badge cs-badge--muted mr-1">tắt</span>
                                        )}
                                        {faq.imageUrls && (
                                            <span className="cs-badge cs-badge--info mr-1">
                                                <i className="fa-solid fa-image" />
                                                {faq.imageUrls.split(',').filter(Boolean).length}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td>{faq.question}</td>
                                <td className="cs-cell-wrap">
                                    {(faq.answer || '').length > 170
                                        ? `${faq.answer.substring(0, 170)}…`
                                        : faq.answer}
                                </td>
                                <td>{faq.category || '—'}</td>
                                <td>
                                    <div className="cs-actions">
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${isEditing ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => this.openEdit(faq)}
                                        >
                                            {isEditing ? 'Đóng' : 'Sửa'}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => this.handleDelete(faq)}
                                        >
                                            Xoá
                                        </button>
                                    </div>
                                </td>
                            </tr>

                            {isEditing && (
                                <tr className="cs-row-editor">
                                    <td colSpan="5">
                                        <div className="cs-inline-editor">
                                            <div className="cs-inline-editor__head">
                                                <h3>
                                                    <i className="fa-solid fa-pen-to-square mr-2" />
                                                    Sửa: <span className="cs-code">{faq.id}</span>
                                                </h3>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-sm"
                                                    onClick={this.closeEditor}
                                                >
                                                    Đóng
                                                </button>
                                            </div>

                                            <div className="cs-inline-editor__body">
                                                {this.renderEditorFields()}
                                            </div>

                                            <div className="cs-inline-editor__foot">
                                                {this.renderEditorActions()}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    renderBulk() {
        const { bulkText, bulkRunning, showBulk } = this.state;

        return (
            <div className="cs-card">
                <div className="cs-card__head">
                    <div>
                        <h2><i className="fa-solid fa-file-import mr-2" />Nạp hàng loạt</h2>
                        <div className="cs-card__desc">
                            Dán một mảng JSON. Mã nào đã có sẽ bị ghi đè, chưa có thì tạo mới.
                        </div>
                    </div>
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => this.setState({ showBulk: !showBulk })}
                    >
                        {showBulk ? 'Thu gọn' : 'Mở'}
                    </button>
                </div>

                {showBulk && (
                    <div className="cs-card__body">
                        <textarea
                            className="form-control"
                            rows="7"
                            style={{ fontFamily: 'SFMono-Regular, Menlo, Consolas, monospace', fontSize: '13px' }}
                            placeholder={'[{"id":"nap-icoin-momo","question":"...","answer":"...","category":"nạp icoin","language":"vi","isActive":true}]'}
                            value={bulkText}
                            onChange={(e) => this.setState({ bulkText: e.target.value })}
                        />
                        <small className="cs-hint">
                            Nên bấm "Chạy thử" trước — đó là thứ duy nhất cho biết bạn sắp tạo mới hay sắp
                            ghi đè lên mục đang có.
                        </small>

                        <div style={{ marginTop: '14px' }}>
                            <button
                                type="button"
                                className="btn btn-outline-primary mr-2"
                                onClick={() => this.handleBulk(true)}
                                disabled={bulkRunning || !bulkText.trim()}
                            >
                                Chạy thử
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => this.handleBulk(false)}
                                disabled={bulkRunning || !bulkText.trim()}
                            >
                                {bulkRunning ? 'Đang nạp…' : 'Nạp thật'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    render() {
        const { loading, faqs } = this.state;
        const visible = this.filteredFaqs();

        return (
            <div>
                {this.renderToolbar()}
                {this.renderCreateEditor()}

                <div className="cs-card">
                    <div className="cs-card__head">
                        <div>
                            <h2>Kho câu trả lời</h2>
                            <div className="cs-card__desc">
                                Sửa ở đây có hiệu lực ngay với chatbot, không phải chờ.
                            </div>
                        </div>
                        {!loading && (
                            <span className="cs-badge cs-badge--info">
                                {visible.length === faqs.length
                                    ? `${faqs.length} mục`
                                    : `${visible.length} / ${faqs.length} mục`}
                            </span>
                        )}
                    </div>

                    <div className="cs-card__body cs-card__body--flush">
                        {this.renderTable(visible)}
                    </div>
                </div>

                {this.renderBulk()}
            </div>
        );
    }
}
